import React, { useState, useEffect } from "react";
import safeLogger from "../utils/safeLogger";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from "../api";
import { useNavigate } from "react-router-dom"; // Importa o hook para navegação

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    registration: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate(); // Hook para navegação entre páginas

  const validateName = (name) => /^[a-zA-ZÀ-ÿ0-9\s]{1,50}$/.test(name);

  const validateRegistration = (registration) =>
    /^[A-Z]{3}-\d{4}-\d{5}$/.test(registration);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[@$!%*?&]/.test(password),
    };
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(Boolean);
  };

  const validateForm = () => {
    const isFormValid =
      validateName(formData.name) &&
      validateRegistration(formData.registration) &&
      validateEmail(formData.email) &&
      validatePassword(formData.password) &&
      formData.password === formData.confirmPassword;
    setIsButtonDisabled(!isFormValid);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    let newErrors = { ...errors };
    if (name === "name") {
      if (!validateName(value)) {
        newErrors.name =
          "O nome deve conter apenas letras e números (máximo 50 caracteres).";
      } else {
        delete newErrors.name;
      }
    }

    if (name === "registration") {
      if (!validateRegistration(value)) {
        newErrors.registration =
          "A matrícula deve estar no formato XXX-XXXX-XXXXX.";
      } else {
        delete newErrors.registration;
      }
    }

    if (name === "email") {
      if (!validateEmail(value)) {
        newErrors.email = "E-mail inválido.";
      } else {
        delete newErrors.email;
      }
    }

    if (name === "password") {
      validatePassword(value); // Atualiza os requisitos em tempo real
      if (!validatePassword(value)) {
        newErrors.password =
          "A senha deve ter ao menos 6 caracteres, incluindo letra maiúscula, minúscula, número e caractere especial.";
      } else {
        delete newErrors.password;
      }
    }

    if (name === "confirmPassword") {
      if (value !== formData.password) {
        newErrors.confirmPassword = "As senhas devem ser iguais.";
      } else {
        delete newErrors.confirmPassword;
      }
    }

    setErrors(newErrors);
  };

  useEffect(() => {
    validateForm(); // Revalida o formulário sempre que o formData ou os erros mudam
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, errors]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleRegister = async () => {
    try {
      const response = await api.post("/auth/register", {
        ...formData,
        registration: formData.registration,
      });
      setSuccessMessage(
        `Cadastro realizado com sucesso! Seu perfil: ${response.data.profile}`
      );
      setErrors({}); // Remove erros, se houverem

      // Define um timeout para limpar a mensagem
      const successTimeout = setTimeout(() => {
        setSuccessMessage(""); // Limpa a mensagem após 3 segundos
      }, 3000);

      // Limpe o formulário após o registro
      setFormData({
        name: "",
        registration: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      // Aguarda 3 segundos e redireciona para a página de login
      setTimeout(() => {
        setSuccessMessage(""); // Limpa a mensagem de sucesso
        navigate("/login"); // Redireciona para a página de login
      }, 3000);

      // Limpa o timeout se o componente for desmontado
      return () => clearTimeout(successTimeout);
    } catch (error) {
      safeLogger.error("Erro ao registrar usuário:", error.message);

      // Captura e exibe a mensagem de erro do backend
      if (error.response?.status === 409) {
        setErrors({ form: "E-mail já cadastrado. Tente usar outro e-mail." });
      } else {
        setErrors({ form: error.response?.data?.error || "Ocorreu um erro." });
      }

      setSuccessMessage(""); // Remove a mensagem de sucesso, se houver

      // Define um timeout para limpar os erros
      const errorTimeout = setTimeout(() => {
        setErrors({});
      }, 3000);

      // Limpa o timeout se o componente for desmontado
      return () => clearTimeout(errorTimeout);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, textAlign: "center" }}>
      <Typography variant="h4" gutterBottom>
        Cadastro
      </Typography>
      <Box sx={{ mt: 2 }}>
        <TextField
          label="Nome"
          name="name"
          variant="outlined"
          fullWidth
          inputProps={{ maxLength: 50 }}
          value={formData.name}
          onChange={handleInputChange}
          error={!!errors.name}
          helperText={
            errors.name && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                {errors.name}
              </span>
            )
          }
          sx={{ mb: 2 }}
        />
        <TextField
          label="Matrícula"
          name="registration"
          variant="outlined"
          fullWidth
          value={formData.registration}
          onChange={handleInputChange}
          error={!!errors.registration}
          helperText={
            errors.registration && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                {errors.registration}
              </span>
            )
          }
          sx={{ mb: 2 }}
        />
        <TextField
          label="E-mail"
          name="email"
          variant="outlined"
          fullWidth
          value={formData.email}
          onChange={handleInputChange}
          error={!!errors.email}
          helperText={
            errors.email && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                {errors.email}
              </span>
            )
          }
          sx={{ mb: 2 }}
        />
        <TextField
          label="Senha"
          name="password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          value={formData.password}
          onChange={handleInputChange}
          error={!!errors.password}
          helperText={
            errors.password && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                {errors.password}
              </span>
            )
          }
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={togglePasswordVisibility}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mb: 2, textAlign: "left" }}>
          <Typography variant="body2">
            <span
              style={{
                color: passwordRequirements.minLength ? "green" : "red",
              }}
            >
              {passwordRequirements.minLength ? "✔" : "✘"} Mínimo de 6
              caracteres
            </span>
          </Typography>
          <Typography variant="body2">
            <span
              style={{
                color: passwordRequirements.uppercase ? "green" : "red",
              }}
            >
              {passwordRequirements.uppercase ? "✔" : "✘"} Pelo menos 1 letra
              maiúscula
            </span>
          </Typography>
          <Typography variant="body2">
            <span
              style={{
                color: passwordRequirements.lowercase ? "green" : "red",
              }}
            >
              {passwordRequirements.lowercase ? "✔" : "✘"} Pelo menos 1 letra
              minúscula
            </span>
          </Typography>
          <Typography variant="body2">
            <span
              style={{
                color: passwordRequirements.number ? "green" : "red",
              }}
            >
              {passwordRequirements.number ? "✔" : "✘"} Pelo menos 1 número
            </span>
          </Typography>
          <Typography variant="body2">
            <span
              style={{
                color: passwordRequirements.specialChar ? "green" : "red",
              }}
            >
              {passwordRequirements.specialChar ? "✔" : "✘"} Pelo menos 1
              caractere especial
            </span>
          </Typography>
        </Box>
        <TextField
          label="Confirmar Senha"
          name="confirmPassword"
          type={showConfirmPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          value={formData.confirmPassword}
          onChange={handleInputChange}
          error={!!errors.confirmPassword}
          helperText={
            errors.confirmPassword && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                {errors.confirmPassword}
              </span>
            )
          }
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={toggleConfirmPasswordVisibility}>
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={isButtonDisabled}
          sx={{ mt: 2 }}
          onClick={handleRegister}
        >
          Cadastrar
        </Button>

        {errors.form && (
          <Typography sx={{ color: "red", fontWeight: "bold", mt: 2 }}>
            {errors.form}
          </Typography>
        )}

        {successMessage && (
          <Typography sx={{ color: "green", fontWeight: "bold", mt: 2 }}>
            {successMessage}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default Register;
